import { ChevronDown, ChevronLeft, ChevronRight, ChevronsDown, ChevronsUp, GripVertical, PanelLeftOpen, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { employeeBelongsToCompany, unitBelongsToCompany } from "../lib/company";
import { useOrgStore } from "../state/orgStore";
import type { CompanyFilter, OrgState, OrgUnit } from "../types/org";

interface SidebarProps {
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}

export function Sidebar({ collapsed, onCollapsedChange }: SidebarProps) {
  const state = useOrgStore((store) => store.state);
  const focusUnit = useOrgStore((store) => store.focusUnit);
  const selectEmployee = useOrgStore((store) => store.selectEmployee);
  const reparentUnit = useOrgStore((store) => store.reparentUnit);
  const [expandedUnits, setExpandedUnits] = useState<Set<string>>(() => new Set());
  const [draggedUnitId, setDraggedUnitId] = useState<string>();
  const previousSidebarTreeKey = useRef<string | undefined>(undefined);

  const company = state.companyFilter ?? "Qisda";
  const companyUnits = useMemo(() => Object.values(state.units).filter((unit) => !unit.isUnlinked && unitBelongsToCompany(state, unit, company)), [company, state]);
  const visibleUnits = useMemo(() => sidebarUnits(state, company), [company, state]);
  const visibleIdSet = useMemo(() => new Set(visibleUnits.map((unit) => unit.id)), [visibleUnits]);
  const sidebarTreeKey = useMemo(() => `${company}:${state.businessUnitFilter ?? ""}:${visibleUnits.map((unit) => `${unit.id}:${unit.parentId ?? ""}`).join("|")}`, [company, state.businessUnitFilter, visibleUnits]);
  const defaultExpandedUnitIds = useMemo(() => defaultSidebarExpandedUnitIds(state, company, visibleUnits, visibleIdSet), [company, state, visibleIdSet, visibleUnits]);
  const roots = useMemo(() => (
    visibleUnits
      .filter((unit) => !unit.parentId || !visibleIdSet.has(unit.parentId))
      .sort(compareUnit)
  ), [visibleIdSet, visibleUnits]);
  const expandableUnitIds = useMemo(() => {
    const parentIds = new Set<string>();
    for (const unit of visibleUnits) {
      if (unit.parentId && visibleIdSet.has(unit.parentId)) parentIds.add(unit.parentId);
    }
    return [...parentIds];
  }, [visibleIdSet, visibleUnits]);
  const collapsedExpandedSet = useMemo(() => new Set(defaultExpandedUnitIds.filter((id) => expandedUnits.has(id))), [defaultExpandedUnitIds, expandedUnits]);
  const allExpandableUnitsOpen = expandableUnitIds.length > 0 && expandableUnitIds.every((id) => expandedUnits.has(id));
  const canCollapseUnits = !setsEqual(expandedUnits, collapsedExpandedSet);
  const unassignedEmployeeIds = useMemo(() => (
    state.unassignedEmployeeIds.filter((id) => employeeBelongsToCompany(state, state.employees[id], company))
  ), [company, state.employees, state.unassignedEmployeeIds]);
  const unlinkedUnitIds = useMemo(() => (
    state.unlinkedUnitIds.filter((id) => unitBelongsToCompany(state, state.units[id], company))
  ), [company, state, state.units, state.unlinkedUnitIds]);

  useEffect(() => {
    const treeChanged = previousSidebarTreeKey.current !== sidebarTreeKey;
    previousSidebarTreeKey.current = sidebarTreeKey;

    setExpandedUnits((current) => {
      if (treeChanged) return new Set(defaultExpandedUnitIds);

      const next = new Set([...current].filter((id) => visibleIdSet.has(id)));
      for (const id of defaultExpandedUnitIds) next.add(id);
      return setsEqual(current, next) ? current : next;
    });
  }, [defaultExpandedUnitIds, sidebarTreeKey, visibleIdSet]);

  function toggleUnit(unitId: string) {
    setExpandedUnits((current) => {
      const next = new Set(current);
      if (next.has(unitId)) next.delete(unitId);
      else next.add(unitId);
      return next;
    });
  }

  function expandAllUnits() {
    setExpandedUnits(new Set([...defaultExpandedUnitIds, ...expandableUnitIds]));
  }

  function collapseAllUnits() {
    setExpandedUnits((current) => new Set(defaultExpandedUnitIds.filter((id) => current.has(id))));
  }

  function dropOnUnit(targetUnitId: string) {
    if (!draggedUnitId || draggedUnitId === targetUnitId) return;
    if (reparentUnit(draggedUnitId, targetUnitId)) focusUnit(draggedUnitId);
    setDraggedUnitId(undefined);
  }

  if (collapsed) {
    return (
      <aside className="sidebar is-collapsed" aria-label="組織單位主檔已收合">
        <button className="sidebar-rail-button" onClick={() => onCollapsedChange(false)} title="展開左側面板" aria-label="展開左側面板">
          <PanelLeftOpen size={18} />
        </button>
      </aside>
    );
  }

  return (
    <aside className="sidebar" aria-label="組織單位主檔">
      <div className="panel-heading">
        <div className="sidebar-heading-title">
          <strong>組織單位</strong>
          <b>{companyUnits.length}</b>
        </div>
        <span>
          <Search size={15} />
          <button className="icon-button tiny" onClick={() => onCollapsedChange(true)} title="收合左側面板" aria-label="收合左側面板">
            <ChevronLeft size={15} />
          </button>
        </span>
      </div>
      <div className="sidebar-body">
        <div className="tree-control-row" aria-label={`${company} 組織樹展開控制`}>
          <button type="button" onClick={expandAllUnits} disabled={!expandableUnitIds.length || allExpandableUnitsOpen}>
            <ChevronsDown size={14} /> 全部展開
          </button>
          <button type="button" onClick={collapseAllUnits} disabled={!canCollapseUnits}>
            <ChevronsUp size={14} /> 全部收合
          </button>
        </div>
        <div className="unit-tree" aria-label={`${company} 組織單位樹`}>
          {roots.map((unit) => (
            <TreeItem
              key={unit.id}
              unitId={unit.id}
              depth={0}
              visibleIdSet={visibleIdSet}
              expandedUnits={expandedUnits}
              draggedUnitId={draggedUnitId}
              onDragStart={setDraggedUnitId}
              onDropOnUnit={dropOnUnit}
              onToggle={toggleUnit}
            />
          ))}
        </div>
        <div className="side-section">
          <div className="section-title">未指派員工<b>{unassignedEmployeeIds.length}</b></div>
          {unassignedEmployeeIds.slice(0, 5).map((id) => {
            const employee = state.employees[id];
            if (!employee) return null;
            return (
              <button key={id} className="mini-person" onClick={() => selectEmployee(id)}>
                <span className="avatar">{initials(employee.chineseName)}</span>
                <span>{employee.englishName || employee.chineseName}<small>{employee.pGrade || "未分級"} {employee.jobTitle || ""}</small></span>
              </button>
            );
          })}
          <button className="link-button">查看全部</button>
        </div>
        <div className="side-section">
          <div className="section-title">未連結單位<b>{unlinkedUnitIds.length}</b></div>
          {unlinkedUnitIds.slice(0, 5).map((id) => state.units[id] ? (
            <button key={id} className="mini-unit" onClick={() => focusUnit(id)}>{state.units[id].nameZh || state.units[id].code}</button>
          ) : null)}
          <button className="link-button">查看全部</button>
        </div>
      </div>
    </aside>
  );
}

function TreeItem({
  unitId,
  depth,
  visibleIdSet,
  expandedUnits,
  draggedUnitId,
  onDragStart,
  onDropOnUnit,
  onToggle,
}: {
  unitId: string;
  depth: number;
  visibleIdSet: Set<string>;
  expandedUnits: Set<string>;
  draggedUnitId?: string;
  onDragStart: (unitId?: string) => void;
  onDropOnUnit: (unitId: string) => void;
  onToggle: (unitId: string) => void;
}) {
  const state = useOrgStore((store) => store.state);
  const focusUnit = useOrgStore((store) => store.focusUnit);
  const unit = state.units[unitId];
  const children = Object.values(state.units)
    .filter((item) => item.parentId === unitId && visibleIdSet.has(item.id))
    .sort(compareUnit);
  if (!unit) return null;
  const hasChildren = children.length > 0;
  const isOpen = expandedUnits.has(unitId);
  const canDrop = Boolean(draggedUnitId && draggedUnitId !== unitId);

  return (
    <div>
      <div
        className={`tree-row ${state.selectedUnitId === unitId ? "active" : ""} ${canDrop ? "is-drop-target" : ""}`}
        style={{ paddingLeft: 10 + depth * 12 }}
        draggable
        onDragStart={(event) => {
          event.dataTransfer.effectAllowed = "move";
          event.dataTransfer.setData("text/plain", unitId);
          onDragStart(unitId);
        }}
        onDragEnd={() => onDragStart(undefined)}
        onDragOver={(event) => {
          if (canDrop) event.preventDefault();
        }}
        onDrop={(event) => {
          event.preventDefault();
          onDropOnUnit(unitId);
        }}
      >
        <GripVertical className="tree-drag-handle" size={13} aria-hidden />
        <button
          className="tree-toggle"
          onClick={(event) => {
            event.stopPropagation();
            if (hasChildren) onToggle(unitId);
          }}
          disabled={!hasChildren}
          aria-label={isOpen ? "收合單位" : "展開單位"}
        >
          {hasChildren ? isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} /> : <span className="tree-spacer" />}
        </button>
        <button className="tree-select" onClick={() => focusUnit(unitId)}>
          <span className="tree-label">{unit.nameZh || unit.nameEn || unit.code}</span>
          <small>{unit.code}</small>
        </button>
      </div>
      {isOpen && children.map((child) => (
        <TreeItem
          key={child.id}
          unitId={child.id}
          depth={depth + 1}
          visibleIdSet={visibleIdSet}
          expandedUnits={expandedUnits}
          draggedUnitId={draggedUnitId}
          onDragStart={onDragStart}
          onDropOnUnit={onDropOnUnit}
          onToggle={onToggle}
        />
      ))}
    </div>
  );
}

function sidebarUnits(state: OrgState, company: CompanyFilter) {
  const units = Object.values(state.units).filter((unit) => !unit.isUnlinked && unitBelongsToCompany(state, unit, company));
  const roots = sidebarRootUnits(state, company, units);
  const rootSubtree = roots.length ? new Set(roots.flatMap((root) => collectDescendants(root.id, units, true))) : undefined;
  const business = state.businessUnitFilter ?? "";
  if (!business) return rootSubtree ? units.filter((unit) => rootSubtree.has(unit.id)) : units;

  const directMatches = new Set<string>();
  const departmentCodes = new Set<string>();
  for (const unit of units) {
    if ([unit.buBg, unit.code, unit.nameZh, unit.nameEn].some((value) => includesToken(value, business))) directMatches.add(unit.id);
  }
  for (const employee of Object.values(state.employees)) {
    if (!employeeBelongsToCompany(state, employee, company)) continue;
    if (!includesToken(employee.buBg, business)) continue;
    if (employee.unitId) directMatches.add(employee.unitId);
    if (employee.departmentCode) departmentCodes.add(employee.departmentCode);
  }
  for (const unit of units) {
    if (departmentCodes.has(unit.code)) directMatches.add(unit.id);
  }
  if (!directMatches.size) return [];

  const allowed = new Set<string>();
  for (const id of directMatches) {
    addAncestors(id, allowed, state, company);
    addDescendants(id, allowed, units);
  }
  return units.filter((unit) => allowed.has(unit.id) && (!rootSubtree || rootSubtree.has(unit.id)));
}

function defaultSidebarExpandedUnitIds(state: OrgState, company: CompanyFilter, visibleUnits: OrgUnit[], visibleIdSet: Set<string>) {
  const expanded = new Set<string>();
  const roots = sidebarRootUnits(state, company, visibleUnits);
  for (const root of roots) {
    if (visibleIdSet.has(root.id) && hasVisibleChildren(root.id, visibleUnits, visibleIdSet)) expanded.add(root.id);
  }

  if (company === "BenQ" && roots[0]) {
    const root = roots[0];
    const d000 = visibleUnits.find((unit) => unit.code.trim().toUpperCase() === "D000" && unit.parentId === root.id);
    if (d000 && hasVisibleChildren(d000.id, visibleUnits, visibleIdSet)) expanded.add(d000.id);
  }

  if (company === "Qisda") {
    for (const unit of visibleUnits) {
      const code = unit.code.trim().toUpperCase();
      if ((code === "E000" || code === "C000") && hasVisibleChildren(unit.id, visibleUnits, visibleIdSet)) expanded.add(unit.id);
    }
  }

  const focused = state.focusedUnitId ? state.units[state.focusedUnitId] : undefined;
  if (focused && visibleIdSet.has(focused.id)) {
    for (const id of ancestorIdsWithinVisibleSet(state, focused, visibleIdSet)) expanded.add(id);
  }

  return [...expanded].sort();
}

function sidebarRootUnits(state: OrgState, company: CompanyFilter, units: OrgUnit[]) {
  if (company === "BQP") {
    const topLevel = units.filter((unit) => !unit.parentId || !unitBelongsToCompany(state, state.units[unit.parentId], company));
    return topLevel.length ? topLevel.sort(compareUnit) : units.filter((unit) => unit.code.trim().toUpperCase() === "P00");
  }
  const root = sidebarRootUnit(state, company, units);
  return root ? [root] : [];
}

function sidebarRootUnit(state: OrgState, company: CompanyFilter, units: OrgUnit[]) {
  if (company === "Qisda") return units.find((unit) => unit.code.trim().toUpperCase() === "E000" && !unit.parentId) ?? units.find((unit) => unit.code.trim().toUpperCase() === "C000");
  return units.find((unit) => unit.code.trim().toUpperCase() === "B000" && !unit.parentId) ?? units.find((unit) => unit.code.trim().toUpperCase() === "B000");
}

function hasVisibleChildren(unitId: string, units: OrgUnit[], visibleIdSet: Set<string>) {
  return units.some((unit) => unit.parentId === unitId && visibleIdSet.has(unit.id));
}

function ancestorIdsWithinVisibleSet(state: OrgState, unit: OrgUnit, visibleIdSet: Set<string>) {
  const ids: string[] = [];
  let current = unit.parentId ? state.units[unit.parentId] : undefined;
  while (current) {
    if (visibleIdSet.has(current.id)) ids.push(current.id);
    current = current.parentId ? state.units[current.parentId] : undefined;
  }
  return ids;
}

function collectDescendants(unitId: string, units: OrgUnit[], includeSelf = false) {
  const ids = includeSelf ? [unitId] : [];
  for (const child of units.filter((unit) => unit.parentId === unitId)) {
    ids.push(child.id, ...collectDescendants(child.id, units));
  }
  return ids;
}

function addAncestors(unitId: string, allowed: Set<string>, state: OrgState, company: CompanyFilter) {
  let current: OrgUnit | undefined = state.units[unitId];
  while (current && unitBelongsToCompany(state, current, company)) {
    allowed.add(current.id);
    current = current.parentId ? state.units[current.parentId] : undefined;
  }
}

function addDescendants(unitId: string, allowed: Set<string>, units: OrgUnit[]) {
  allowed.add(unitId);
  for (const child of units.filter((unit) => unit.parentId === unitId)) addDescendants(child.id, allowed, units);
}

function includesToken(value: unknown, token: string) {
  return String(value ?? "").toLowerCase().includes(token.trim().toLowerCase());
}

function setsEqual(left: Set<string>, right: Set<string>) {
  if (left.size !== right.size) return false;
  for (const value of left) {
    if (!right.has(value)) return false;
  }
  return true;
}

function initials(name: string) {
  return name.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase();
}

function compareUnit(a: OrgUnit, b: OrgUnit) {
  return a.code.localeCompare(b.code, undefined, { numeric: true }) || a.level - b.level || a.nameZh.localeCompare(b.nameZh);
}
